/**
 * ui/AuthProvider — session (JWT Bearer) + garde `mustChangePassword` (A4).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * AUTH-UI — candidat `@mostajs/auth-ui` (N2, auth-stack, D15). NEUTRE :
 *  • `apiConfig` (dont `baseUrl`) et `tokenKey` sont INJECTÉS par l'app hôte —
 *    aucune lecture d'env de framework, aucun nom en dur propre à une app ;
 *  • transport-agnostique (compatible auth maison JWT/Bearer, D4, comme @mostajs/auth).
 * Autorisation fine (rôles/permissions) → @mostajs/rbac.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { createAdminApi } from '../engine/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children, apiConfig, tokenKey = 'admin-token' }) {
  const [token, setToken] = useState(() => localStorage.getItem(tokenKey) || null);
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(() => Boolean(localStorage.getItem(tokenKey)));

  // Un seul client (moteur), baseUrl/paths injectés, lié au jeton courant.
  const api = useMemo(
    () => createAdminApi({
      baseUrl: apiConfig?.baseUrl,
      paths: apiConfig?.paths,
      getToken: () => localStorage.getItem(tokenKey),
    }),
    [apiConfig, tokenKey],
  );

  // Réhydratation au démarrage : jeton persistant → récupère l'utilisateur courant
  // (rôle + mustChangePassword) via `me()` ; jeton invalide/expiré → purge.
  useEffect(() => {
    if (!token) { setBooting(false); return; }
    let alive = true;
    api.me()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => {
        if (!alive) return;
        localStorage.removeItem(tokenKey);
        setToken(null);
        setUser(null);
      })
      .finally(() => { if (alive) setBooting(false); });
    return () => { alive = false; };
  }, [api, token, tokenKey]);

  const login = useCallback(async (username, password) => {
    const { token: tk, user: u } = await api.login(username, password);
    localStorage.setItem(tokenKey, tk);
    setToken(tk);
    setUser(u);
    return u;
  }, [api, tokenKey]);

  const logout = useCallback(() => {
    localStorage.removeItem(tokenKey);
    setToken(null);
    setUser(null);
  }, [tokenKey]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.changePassword(currentPassword, newPassword);
    // A4 — le drapeau retombe côté serveur ; on le reflète localement.
    setUser((u) => (u ? { ...u, mustChangePassword: false } : u));
  }, [api]);

  const value = useMemo(() => ({
    api,
    token,
    user,
    booting,
    isAuthenticated: Boolean(token),
    mustChangePassword: Boolean(user?.mustChangePassword),
    role: user?.role ?? null,
    login,
    logout,
    changePassword,
  }), [api, token, user, booting, login, logout, changePassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
}
